/**
 * Strategies — browse strategy types, create instances, activate/deactivate,
 * watch live signals and (optionally) let a strategy trade on its own.
 *
 * Activation subscribes the broker feed to the strategy's instruments, so
 * signals flow without Market Watch open. Auto-executed orders still pass the
 * risk engine on the backend.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  BarChart3,
  Bot,
  Brain,
  Layers,
  Pause,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Trash2,
  TrendingUp,
  Wifi,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, getErrorMessage } from '@/config/api';
import { cn } from '@/lib/cn';
import { formatDateTime, formatINR, formatQty, formatTime } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  Switch,
  Table,
  TableMessage,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
} from '@/components/ui';
import type { Tone } from '@/components/ui';
import { InstrumentSearch } from '@/features/market/components/InstrumentSearch';
import type { InstrumentResult } from '@/features/market/components/InstrumentSearch';

// ── Types ───────────────────────────────────────────────────────────────────

interface StrategyInstrument {
  token: string;
  symbol: string;
  segment: string;
}

interface StrategyRuntime {
  activated_at: string;
  feed_subscribed: boolean;
  last_tick_at: string | null;
  ticks: number;
  signals_today: number;
  orders_today: number;
  last_signal: Record<string, unknown> | null;
  last_error: string;
}

interface StrategyData {
  strategy_id: string;
  strategy_type: string;
  name: string;
  instruments: StrategyInstrument[];
  parameters: Record<string, unknown>;
  max_position_size: number;
  max_loss_per_trade: string;
  max_daily_trades: number;
  auto_execute: boolean;
  order_quantity: number;
  product: string;
  order_type: string;
  signal_cooldown_sec: number;
  status: string;
  created_at: string;
  updated_at: string;
  runtime?: StrategyRuntime | null;
}

interface StrategyTypeInfo {
  type: string;
  class_name: string;
  default_parameters: Record<string, unknown>;
  parameter_schema: Record<string, unknown>;
}

interface SignalExecution {
  order_id?: string;
  status?: string;
  broker_order_id?: string;
  skipped?: string;
  message?: string;
  violations?: string[];
}

interface SignalData {
  signal_id: string;
  strategy_id: string;
  strategy_name: string;
  signal_type: string;
  trading_symbol: string;
  exchange_segment: string;
  suggested_price: string;
  stop_loss: string;
  target: string;
  confidence: number;
  reason: string;
  metadata: Record<string, unknown>;
  timestamp: string;
  execution: SignalExecution | null;
}

const createFormSchema = z.object({
  strategy_type: z.string().min(1, 'Required'),
  name: z.string().min(1, 'Required').max(128),
  parameters_json: z.string().default('{}'),
  max_position_size: z.coerce.number().int().min(0).default(0),
  max_loss_per_trade: z.coerce.number().min(0).default(0),
  max_daily_trades: z.coerce.number().int().min(0).default(0),
  order_quantity: z.coerce.number().int().min(0).default(0),
  product: z.enum(['MIS', 'CNC', 'NRML']).default('MIS'),
  order_type: z.enum(['MKT', 'L']).default('MKT'),
  signal_cooldown_sec: z.coerce.number().int().min(0).max(86400).default(60),
});

type CreateFormData = z.infer<typeof createFormSchema>;

const STATUS_TONE: Record<string, { tone: Tone; label: string }> = {
  inactive: { tone: 'neutral', label: 'Inactive' },
  active: { tone: 'success', label: 'Active' },
  paused: { tone: 'warning', label: 'Paused · re-activate' },
  error: { tone: 'danger', label: 'Error' },
};

const STRATEGY_ICONS: Record<string, LucideIcon> = {
  ema_crossover: TrendingUp,
  rsi_mean_reversion: BarChart3,
  macd_momentum: Zap,
  bollinger_bands: Activity,
  vwap: BarChart3,
  oi_buildup: Layers,
};

const TYPE_LABELS: Record<string, string> = {
  oi_buildup: 'OI Build-up',
  ema_crossover: 'EMA Crossover',
  rsi_mean_reversion: 'RSI Mean Reversion',
  macd_momentum: 'MACD Momentum',
  bollinger_bands: 'Bollinger Bands',
  vwap: 'VWAP',
};

const TYPE_HINTS: Record<string, string> = {
  oi_buildup:
    'Reads price and open interest together (long/short build-up, short covering, long unwinding). Needs F&O instruments — OI is not published for cash equities.',
  ema_crossover: 'Fast EMA crossing the slow EMA.',
  rsi_mean_reversion: 'RSI leaving oversold/overbought zones.',
  macd_momentum: 'MACD line crossing its signal line.',
  bollinger_bands: 'Price reverting from the bands.',
  vwap: 'Price crossing intraday VWAP.',
};

function prettyType(type: string): string {
  return TYPE_LABELS[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function describeExecution(exec: SignalExecution | null): { label: string; tone: Tone } {
  if (!exec) return { label: 'Advisory', tone: 'neutral' };
  if (exec.order_id) {
    const status = exec.status ?? 'sent';
    if (status === 'rejected') return { label: `Order rejected${exec.violations?.length ? ' (risk)' : ''}`, tone: 'danger' };
    return { label: `Order ${status}${exec.broker_order_id ? ` · #${exec.broker_order_id}` : ''}`, tone: 'success' };
  }
  switch (exec.skipped) {
    case 'daily_trade_cap':
      return { label: 'Daily cap reached', tone: 'warning' };
    case 'no_quantity':
      return { label: 'No order quantity set', tone: 'warning' };
    case 'error':
      return { label: `Error: ${exec.message ?? ''}`, tone: 'danger' };
    default:
      return { label: exec.message || exec.skipped || 'Not executed', tone: 'neutral' };
  }
}

const SIGNAL_POLL_MS = 5_000;

export default function StrategiesPage() {
  const [strategies, setStrategies] = useState<StrategyData[]>([]);
  const [types, setTypes] = useState<StrategyTypeInfo[]>([]);
  const [signals, setSignals] = useState<SignalData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StrategyData | null>(null);
  const [autoExecute, setAutoExecute] = useState(false);
  const [instruments, setInstruments] = useState<StrategyInstrument[]>([]);
  const [showPicker, setShowPicker] = useState(false);

  const fetchStrategies = useCallback(async () => {
    try {
      const { data } = await api.get('/strategies');
      setStrategies(data);
      setLoadError(null);
    } catch (err) {
      setLoadError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTypes = useCallback(async () => {
    try {
      const { data } = await api.get('/strategies/types');
      setTypes(data);
    } catch {
      /* type catalogue is optional for rendering the list */
    }
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      const { data } = await api.get('/strategies/signals', { params: { limit: 30 } });
      setSignals(data);
    } catch {
      /* the signal tape is supplementary; the strategy list already reports errors */
    }
  }, []);

  useEffect(() => {
    fetchStrategies();
    fetchTypes();
    fetchSignals();
  }, [fetchStrategies, fetchTypes, fetchSignals]);

  const anyActive = strategies.some((s) => s.status === 'active');

  useEffect(() => {
    if (!anyActive) return;
    const timer = setInterval(() => {
      fetchSignals();
      fetchStrategies();
    }, SIGNAL_POLL_MS);
    return () => clearInterval(timer);
  }, [anyActive, fetchSignals, fetchStrategies]);

  const runAction = async (id: string, action: 'activate' | 'deactivate') => {
    setBusyId(id);
    setSubmitMsg(null);
    try {
      const { data } = await api.post<StrategyData>(`/strategies/${id}/${action}`);
      if (action === 'activate') {
        const subscribed = data.runtime?.feed_subscribed;
        setSubmitMsg({
          ok: true,
          msg: subscribed
            ? `${data.name} is live — feed subscribed to ${data.instruments.length} instrument(s).`
            : `${data.name} is active, but the broker feed could not be subscribed. Connect your broker in Settings and re-activate.`,
        });
      }
      await fetchStrategies();
    } catch (err) {
      setSubmitMsg({ ok: false, msg: getErrorMessage(err) });
    } finally {
      setBusyId(null);
    }
  };

  const toggleAutoExecute = async (strategy: StrategyData, value: boolean) => {
    setBusyId(strategy.strategy_id);
    try {
      await api.put(`/strategies/${strategy.strategy_id}`, { auto_execute: value });
      await fetchStrategies();
    } catch (err) {
      setSubmitMsg({ ok: false, msg: getErrorMessage(err) });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.strategy_id);
    try {
      await api.delete(`/strategies/${deleteTarget.strategy_id}`);
      setDeleteTarget(null);
      await fetchStrategies();
    } catch (err) {
      setSubmitMsg({ ok: false, msg: getErrorMessage(err) });
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  };

  // ── Create form ───────────────────────────────────────────────────────

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateFormData>({
    resolver: zodResolver(createFormSchema) as never,
    defaultValues: {
      strategy_type: '',
      name: '',
      parameters_json: '{}',
      max_position_size: 0,
      max_loss_per_trade: 0,
      max_daily_trades: 0,
      order_quantity: 0,
      product: 'MIS',
      order_type: 'MKT',
      signal_cooldown_sec: 60,
    },
  });

  const selectedType = watch('strategy_type');
  const orderQuantity = watch('order_quantity');

  useEffect(() => {
    const info = types.find((t) => t.type === selectedType);
    if (info) setValue('parameters_json', JSON.stringify(info.default_parameters, null, 2));
    if (selectedType === 'oi_buildup') setValue('product', 'NRML');
  }, [selectedType, types, setValue]);

  const addInstrument = (r: InstrumentResult) => {
    setInstruments((list) =>
      list.some((i) => i.token === r.instrument_token)
        ? list
        : [...list, { token: r.instrument_token, symbol: r.trading_symbol, segment: r.exchange_segment }]
    );
    if (r.lot_size > 1 && Number(orderQuantity) === 0) setValue('order_quantity', r.lot_size);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setShowPicker(false);
  };

  const onSubmit = async (data: CreateFormData) => {
    setIsSubmitting(true);
    setSubmitMsg(null);
    try {
      const parameters = JSON.parse(data.parameters_json);
      await api.post('/strategies', {
        strategy_type: data.strategy_type,
        name: data.name,
        instruments,
        parameters,
        max_position_size: data.max_position_size,
        max_loss_per_trade: data.max_loss_per_trade,
        max_daily_trades: data.max_daily_trades,
        auto_execute: autoExecute,
        order_quantity: data.order_quantity,
        product: data.product,
        order_type: data.order_type,
        signal_cooldown_sec: data.signal_cooldown_sec,
      });
      setSubmitMsg({ ok: true, msg: 'Strategy created. Activate it to start receiving ticks.' });
      reset();
      setInstruments([]);
      setAutoExecute(false);
      closeCreate();
      fetchStrategies();
    } catch (err) {
      const message = err instanceof SyntaxError ? 'Parameters must be valid JSON' : getErrorMessage(err);
      setSubmitMsg({ ok: false, msg: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeCount = strategies.filter((s) => s.status === 'active').length;
  const autoCount = strategies.filter((s) => s.auto_execute).length;
  const signalsToday = useMemo(() => {
    const today = new Date().toDateString();
    return signals.filter((s) => new Date(s.timestamp).toDateString() === today).length;
  }, [signals]);

  const oiSelected = selectedType === 'oi_buildup';
  const hasCashInstrumentForOi = oiSelected && instruments.some((i) => !i.segment.endsWith('_fo'));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Brain}
        title="Strategies"
        description="Automated signal generators — execution still passes the risk engine"
        actions={
          <>
            <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => { fetchStrategies(); fetchSignals(); }}>
              Refresh
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowCreate(true)}>
              New strategy
            </Button>
          </>
        }
      />

      {submitMsg && (
        <Alert tone={submitMsg.ok ? 'success' : 'danger'} onDismiss={() => setSubmitMsg(null)}>
          {submitMsg.msg}
        </Alert>
      )}

      {loadError && (
        <Alert
          tone="danger"
          title="Couldn't load strategies"
          action={
            <Button variant="secondary" size="sm" onClick={fetchStrategies}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard label="Strategies" value={strategies.length} loading={isLoading} icon={Brain} />
        <StatCard label="Active" value={activeCount} loading={isLoading} icon={Play} />
        <StatCard label="Auto-trading" value={autoCount} loading={isLoading} icon={Bot} />
        <StatCard label="Signals today" value={signalsToday} loading={isLoading} icon={Radio} />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : strategies.length === 0 ? (
        <Card>
          <EmptyState
            icon={Brain}
            title="No strategies yet"
            description="Create one from a built-in type — try OI Build-up on a NIFTY future — then activate it to start generating signals."
            action={
              <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowCreate(true)}>
                New strategy
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {strategies.map((strategy) => {
            const Icon = STRATEGY_ICONS[strategy.strategy_type] ?? Brain;
            const status = STATUS_TONE[strategy.status] ?? STATUS_TONE.inactive;
            const isActive = strategy.status === 'active';
            const busy = busyId === strategy.strategy_id;
            const rt = strategy.runtime;
            const qty = strategy.order_quantity || strategy.max_position_size;

            return (
              <Card key={strategy.strategy_id}>
                <CardHeader
                  title={
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-brand-fg" aria-hidden="true" />
                      {strategy.name}
                    </span>
                  }
                  description={`${prettyType(strategy.strategy_type)} · created ${formatDateTime(strategy.created_at)}`}
                  actions={
                    <>
                      {strategy.auto_execute && (
                        <Badge tone="brand" icon={Bot}>
                          Auto
                        </Badge>
                      )}
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </>
                  }
                />
                <CardBody className="flex flex-col gap-4">
                  {isActive && rt && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs">
                      <span className={cn('inline-flex items-center gap-1', rt.feed_subscribed ? 'text-success' : 'text-warning')}>
                        {rt.feed_subscribed ? <Wifi className="h-3.5 w-3.5" aria-hidden="true" /> : <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />}
                        {rt.feed_subscribed ? 'Feed live' : 'Feed not subscribed'}
                      </span>
                      <span className="text-fg-subtle">
                        Last tick{' '}
                        <span className="font-tabular text-fg-muted">{rt.last_tick_at ? formatTime(rt.last_tick_at) : '—'}</span>
                      </span>
                      <span className="text-fg-subtle">
                        Ticks <span className="font-tabular text-fg-muted">{formatQty(rt.ticks)}</span>
                      </span>
                      <span className="text-fg-subtle">
                        Signals today <span className="font-tabular text-fg-muted">{rt.signals_today}</span>
                      </span>
                      <span className="text-fg-subtle">
                        Orders today <span className="font-tabular text-fg-muted">{rt.orders_today}</span>
                      </span>
                      {rt.last_error && <span className="w-full text-danger">{rt.last_error}</span>}
                    </div>
                  )}

                  <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                    <div>
                      <dt className="text-xs text-fg-faint">Order qty</dt>
                      <dd className="font-tabular text-fg-muted">{qty > 0 ? formatQty(qty) : '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-faint">Product / type</dt>
                      <dd className="font-tabular text-fg-muted">
                        {strategy.product} · {strategy.order_type}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-faint">Max trades/day</dt>
                      <dd className="font-tabular text-fg-muted">{strategy.max_daily_trades || '∞'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-faint">Max loss/trade</dt>
                      <dd className="font-tabular text-fg-muted">
                        {Number(strategy.max_loss_per_trade) > 0 ? formatINR(strategy.max_loss_per_trade) : '—'}
                      </dd>
                    </div>
                  </dl>

                  <div>
                    <p className="mb-1.5 text-xs text-fg-faint">Instruments ({strategy.instruments?.length ?? 0})</p>
                    {strategy.instruments?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {strategy.instruments.map((inst) => (
                          <Badge key={inst.token} tone="neutral">
                            {inst.symbol || inst.token}
                            <span className="text-fg-faint uppercase"> {inst.segment?.replace('_', ' ')}</span>
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-warning">No instruments attached — this strategy will never receive a tick.</p>
                    )}
                  </div>

                  <details className="group">
                    <summary className="cursor-pointer text-xs text-fg-subtle transition-colors hover:text-fg-muted">
                      Parameters
                    </summary>
                    <pre className="mt-2 max-h-40 overflow-auto rounded-lg border border-line bg-surface-2 p-3 font-tabular text-xs text-fg-muted">
                      {JSON.stringify(strategy.parameters, null, 2)}
                    </pre>
                  </details>

                  <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
                    <Button
                      variant={isActive ? 'secondary' : 'success'}
                      size="sm"
                      icon={isActive ? Pause : Play}
                      loading={busy}
                      onClick={() => runAction(strategy.strategy_id, isActive ? 'deactivate' : 'activate')}
                    >
                      {isActive ? 'Deactivate' : strategy.status === 'paused' ? 'Re-activate' : 'Activate'}
                    </Button>
                    <Switch
                      checked={strategy.auto_execute}
                      disabled={busy}
                      tone="danger"
                      label={strategy.auto_execute ? 'Auto-trade on' : 'Auto-trade off'}
                      onChange={(v) => toggleAutoExecute(strategy, v)}
                    />
                    <div className="ml-auto">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        onClick={() => setDeleteTarget(strategy)}
                        aria-label={`Delete ${strategy.name}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* Signal tape */}
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-brand-fg" aria-hidden="true" />
              Live signals
            </span>
          }
          description={anyActive ? 'Refreshes every 5s while a strategy is active' : 'Activate a strategy to start the tape'}
        />
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Time</TH>
                <TH>Strategy</TH>
                <TH>Instrument</TH>
                <TH>Signal</TH>
                <TH align="right">Price</TH>
                <TH align="right">SL / Target</TH>
                <TH align="right">Confidence</TH>
                <TH>Reason</TH>
                <TH>Execution</TH>
              </TR>
            </THead>
            <TBody>
              {signals.length === 0 ? (
                <TableMessage colSpan={9}>
                  <EmptyState
                    icon={Radio}
                    title="No signals yet"
                    description="Signals appear here as soon as an active strategy sees a setup on one of its instruments."
                  />
                </TableMessage>
              ) : (
                signals.map((s) => {
                  const buy = s.signal_type === 'BUY' || s.signal_type === 'EXIT_SHORT';
                  const exec = describeExecution(s.execution);
                  const buildup = typeof s.metadata?.buildup === 'string' ? (s.metadata.buildup as string) : null;
                  return (
                    <TR key={s.signal_id}>
                      <TD className="font-tabular text-xs text-fg-subtle">{formatDateTime(s.timestamp)}</TD>
                      <TD className="text-xs text-fg-muted">{s.strategy_name}</TD>
                      <TD>
                        <div className="font-medium text-fg">{s.trading_symbol}</div>
                        <div className="font-tabular text-xs text-fg-faint uppercase">{s.exchange_segment?.replace('_', ' ')}</div>
                      </TD>
                      <TD>
                        <span className={cn('text-xs font-semibold', buy ? 'text-buy' : 'text-sell')}>{s.signal_type}</span>
                        {buildup && <div className="text-xs text-fg-faint">{buildup.replace(/_/g, ' ')}</div>}
                      </TD>
                      <TD align="right" className="font-tabular text-fg">
                        {formatINR(s.suggested_price)}
                      </TD>
                      <TD align="right" className="font-tabular text-xs text-fg-muted">
                        <span className="text-loss">{formatINR(s.stop_loss)}</span>
                        {' / '}
                        <span className="text-profit">{formatINR(s.target)}</span>
                      </TD>
                      <TD align="right" className="font-tabular text-fg-muted">
                        {Math.round(s.confidence * 100)}%
                      </TD>
                      <TD className="max-w-72 text-xs whitespace-normal text-fg-muted">{s.reason}</TD>
                      <TD>
                        <Badge tone={exec.tone}>{exec.label}</Badge>
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableWrap>
      </Card>

      {/* Create */}
      <Modal
        open={showCreate}
        onClose={closeCreate}
        title="New strategy"
        description="Pick a type, attach instruments, and decide whether it may trade on its own."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={closeCreate}>
              Cancel
            </Button>
            <Button type="submit" form="strategy-form" variant="primary" loading={isSubmitting}>
              Create strategy
            </Button>
          </>
        }
      >
        <form id="strategy-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Strategy type" error={errors.strategy_type?.message} required hint={TYPE_HINTS[selectedType]}>
              {(field) => (
                <Select {...field} {...register('strategy_type')}>
                  <option value="">Select a type…</option>
                  {types.map((t) => (
                    <option key={t.type} value={t.type}>
                      {prettyType(t.type)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Name" error={errors.name?.message} required>
              {(field) => <Input {...field} placeholder={oiSelected ? 'NIFTY OI build-up' : 'Morning EMA cross'} {...register('name')} />}
            </Field>
          </div>

          {/* Instruments */}
          <div className="rounded-lg border border-line p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium text-fg-muted">Instruments ({instruments.length})</p>
              <Button variant="secondary" size="sm" icon={Plus} onClick={() => setShowPicker((v) => !v)} aria-expanded={showPicker}>
                {showPicker ? 'Done' : 'Add instrument'}
              </Button>
            </div>
            {instruments.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {instruments.map((inst) => (
                  <span
                    key={inst.token}
                    className="inline-flex items-center gap-1 rounded-md border border-line-strong bg-surface-3 px-2 py-0.5 text-xs text-fg-muted"
                  >
                    {inst.symbol || inst.token}
                    <span className="text-fg-faint uppercase">{inst.segment.replace('_', ' ')}</span>
                    <button
                      type="button"
                      className="rounded p-0.5 text-fg-faint hover:text-fg"
                      onClick={() => setInstruments((list) => list.filter((i) => i.token !== inst.token))}
                      aria-label={`Remove ${inst.symbol || inst.token}`}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="mb-2 text-xs text-fg-subtle">
                {oiSelected ? 'Add a future or option (NSE F&O) — OI is only published for derivatives.' : 'Add at least one instrument to receive ticks.'}
              </p>
            )}
            {hasCashInstrumentForOi && (
              <Alert tone="warning">A cash-segment instrument was added to an OI strategy; it will never produce a signal.</Alert>
            )}
            {showPicker && (
              <div className="mt-2 border-t border-line pt-3">
                <InstrumentSearch
                  onSelect={addInstrument}
                  actionLabel="Add"
                  defaultSegment={oiSelected ? 'nse_fo' : 'nse_cm'}
                  selectedTokens={instruments.map((i) => i.token)}
                  autoFocus={false}
                />
              </div>
            )}
          </div>

          {/* Execution */}
          <div className="rounded-lg border border-line p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-medium text-fg-muted">Execution</p>
                <p className="text-xs text-fg-faint">
                  {autoExecute
                    ? 'Signals become orders automatically (risk engine still applies).'
                    : 'Signals are advisory — place trades yourself from the signal tape.'}
                </p>
              </div>
              <Switch checked={autoExecute} onChange={setAutoExecute} tone="danger" label={autoExecute ? 'Auto-trade on' : 'Auto-trade off'} />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Order quantity" error={errors.order_quantity?.message} hint="Per signal (lots × lot size)">
                {(field) => <Input {...field} type="number" min={0} mono {...register('order_quantity')} />}
              </Field>
              <Field label="Product" error={errors.product?.message}>
                {(field) => (
                  <Select {...field} {...register('product')}>
                    <option value="MIS">MIS</option>
                    <option value="NRML">NRML</option>
                    <option value="CNC">CNC</option>
                  </Select>
                )}
              </Field>
              <Field label="Order type" error={errors.order_type?.message}>
                {(field) => (
                  <Select {...field} {...register('order_type')}>
                    <option value="MKT">Market</option>
                    <option value="L">Limit at signal price</option>
                  </Select>
                )}
              </Field>
              <Field label="Cooldown (s)" error={errors.signal_cooldown_sec?.message} hint="Between signals per symbol">
                {(field) => <Input {...field} type="number" min={0} mono {...register('signal_cooldown_sec')} />}
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Max position size" error={errors.max_position_size?.message} hint="Fallback quantity">
              {(field) => <Input {...field} type="number" min={0} mono {...register('max_position_size')} />}
            </Field>
            <Field label="Max loss per trade (₹)" error={errors.max_loss_per_trade?.message}>
              {(field) => <Input {...field} type="number" min={0} mono {...register('max_loss_per_trade')} />}
            </Field>
            <Field label="Max trades per day" error={errors.max_daily_trades?.message} hint="0 = unlimited">
              {(field) => <Input {...field} type="number" min={0} mono {...register('max_daily_trades')} />}
            </Field>
          </div>

          <Field label="Parameters" error={errors.parameters_json?.message} hint="JSON object — pre-filled from the type's defaults">
            {(field) => <Textarea {...field} className="font-tabular" rows={6} {...register('parameters_json')} />}
          </Field>
        </form>
      </Modal>

      {/* Delete */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete this strategy?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Keep it
            </Button>
            <Button variant="danger" loading={busyId === deleteTarget?.strategy_id} onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          <span className="font-medium text-fg">{deleteTarget?.name}</span> will be removed and stops generating
          signals. Open orders it already placed are not cancelled. This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
